import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { ChevronDown, X, Search, Play, ChevronUp } from 'lucide-react'
import { advertiserApi, Advertiser } from '../../../lib/api/advertisers'
import { campaignApi } from '../../../lib/api/campaigns'
import { Campaign } from '../../../types'
import { AdUnit } from '../../../lib/api/adUnits'
import { adUnitsApi } from '../../../lib/api/adUnits'
import { ReportBreakdown } from '../../../types/reports'

interface TimezoneOption {
  value: string
  label: string
  offset: string
}

let _allTimezones: TimezoneOption[] | null = null
function getAllTimezones(): TimezoneOption[] {
  if (_allTimezones) return _allTimezones
  try {
    const zones = (Intl as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf?.('timeZone') ?? []
    _allTimezones = zones.map(tz => {
      try {
        const offset = new Intl.DateTimeFormat('en', {
          timeZone: tz,
          timeZoneName: 'shortOffset',
        }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? ''
        return { value: tz, label: tz.replace(/_/g, ' '), offset }
      } catch {
        return { value: tz, label: tz, offset: '' }
      }
    })
  } catch {
    _allTimezones = []
  }
  return _allTimezones
}
import { BreakdownSelector } from './BreakdownSelector'
import { MetricSelector } from './MetricSelector'
import { useRole } from '../../../hooks/useRole'

interface Props {
  selectedAdvertiser: Advertiser | null
  onAdvertiserChange: (a: Advertiser | null) => void
  selectedCampaignIds: number[]
  onCampaignIdsChange: (ids: number[]) => void
  selectedAdUnitIds: number[]
  onAdUnitIdsChange: (ids: number[]) => void
  breakdowns: ReportBreakdown[]
  onBreakdownsChange: (b: ReportBreakdown[]) => void
  selectedMetrics: string[]
  onMetricsChange: (m: string[]) => void
  dateFrom: string
  dateTo: string
  onDateFromChange: (d: string) => void
  onDateToChange: (d: string) => void
  timezone: string
  onTimezoneChange: (tz: string) => void
  onRunReport: () => void
  isLoading: boolean
}

type DatePreset = 'today' | '7d' | '30d' | '90d' | 'custom'

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function getPresetDates(preset: DatePreset): { from: string; to: string } | null {
  const today = new Date()
  const to = toIsoDate(today)
  if (preset === 'today') return { from: to, to }
  if (preset === '7d') {
    const from = new Date(today); from.setDate(today.getDate() - 7)
    return { from: toIsoDate(from), to }
  }
  if (preset === '30d') {
    const from = new Date(today); from.setDate(today.getDate() - 30)
    return { from: toIsoDate(from), to }
  }
  if (preset === '90d') {
    const from = new Date(today); from.setDate(today.getDate() - 90)
    return { from: toIsoDate(from), to }
  }
  return null
}

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: '90d', label: 'Last 90d' },
  { value: 'custom', label: 'Custom' },
]

export function ReportFiltersBar({
  selectedAdvertiser,
  onAdvertiserChange,
  selectedCampaignIds,
  onCampaignIdsChange,
  selectedAdUnitIds,
  onAdUnitIdsChange,
  breakdowns,
  onBreakdownsChange,
  selectedMetrics,
  onMetricsChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  timezone,
  onTimezoneChange,
  onRunReport,
  isLoading,
}: Props) {
  const { isAdmin } = useRole()
  const isAdminUser = isAdmin()
  const [collapsed, setCollapsed] = useState(false)
  const [datePreset, setDatePreset] = useState<DatePreset>('30d')

  // Advertiser dropdown state
  const [advertiserOpen, setAdvertiserOpen] = useState(false)
  const [advertiserSearch, setAdvertiserSearch] = useState('')
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([])
  const [isLoadingAdvertisers, setIsLoadingAdvertisers] = useState(false)
  const advertiserRef = useRef<HTMLDivElement>(null)
  const advertiserSearchTimer = useRef<NodeJS.Timeout | null>(null)

  // Campaign dropdown state
  const [campaignOpen, setCampaignOpen] = useState(false)
  const [campaignSearch, setCampaignSearch] = useState('')
  const [debouncedCampaignSearch, setDebouncedCampaignSearch] = useState('')
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)
  const campaignRef = useRef<HTMLDivElement>(null)

  // Ad Unit dropdown state
  const [adUnitOpen, setAdUnitOpen] = useState(false)
  const [adUnitSearch, setAdUnitSearch] = useState('')
  const [adUnits, setAdUnits] = useState<AdUnit[]>([])
  const [isLoadingAdUnits, setIsLoadingAdUnits] = useState(false)
  const adUnitRef = useRef<HTMLDivElement>(null)

  // Timezone dropdown state
  const [timezoneOpen, setTimezoneOpen] = useState(false)
  const [timezoneSearch, setTimezoneSearch] = useState('')
  const timezoneRef = useRef<HTMLDivElement>(null)

  const filteredTimezones = useMemo(() => {
    if (!timezoneOpen) return []
    const zones = getAllTimezones()
    if (!timezoneSearch) return zones
    const q = timezoneSearch.toLowerCase()
    return zones.filter(tz =>
      tz.value.toLowerCase().includes(q) || tz.offset.toLowerCase().includes(q)
    )
  }, [timezoneOpen, timezoneSearch])

  const timezoneDisplayLabel = (() => {
    if (!timezone) return 'Select timezone'
    const city = timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone
    const opt = _allTimezones?.find(t => t.value === timezone)
    return opt ? `${city} (${opt.offset})` : city
  })()

  // Close dropdowns on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (advertiserRef.current && !advertiserRef.current.contains(e.target as Node)) {
        setAdvertiserOpen(false)
      }
      if (campaignRef.current && !campaignRef.current.contains(e.target as Node)) {
        setCampaignOpen(false)
      }
      if (adUnitRef.current && !adUnitRef.current.contains(e.target as Node)) {
        setAdUnitOpen(false)
      }
      if (timezoneRef.current && !timezoneRef.current.contains(e.target as Node)) {
        setTimezoneOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  // Fetch advertisers (admin only; user's advertiser is pre-selected)
  const fetchAdvertisers = useCallback(async (q: string) => {
    setIsLoadingAdvertisers(true)
    try {
      const trimmed = q.trim().replace(/^#/, '')
      const isNumericId = /^\d+$/.test(trimmed)
      const [nameResults, idResult] = await Promise.all([
        advertiserApi.getAdvertisers({ q: trimmed || undefined, per_page: 25 }),
        isNumericId
          ? advertiserApi.getAdvertiser(Number(trimmed))
              .then(a => a ? [a] : [])
              .catch(err => {
                console.warn('Advertiser ID lookup failed', err)
                return [] as Advertiser[]
              })
          : Promise.resolve([] as Advertiser[]),
      ])
      const merged = [...idResult, ...nameResults.advertisers]
      const seen = new Set<number>()
      setAdvertisers(merged.filter(a => a && (seen.has(a.id) ? false : (seen.add(a.id), true))))
    } catch (err) {
      console.warn('Advertiser search failed', err)
    } finally {
      setIsLoadingAdvertisers(false)
    }
  }, [])

  useEffect(() => {
    if (isAdminUser && advertiserOpen) {
      if (advertiserSearchTimer.current) clearTimeout(advertiserSearchTimer.current)
      advertiserSearchTimer.current = setTimeout(() => {
        fetchAdvertisers(advertiserSearch)
      }, 300)
    }
    return () => { if (advertiserSearchTimer.current) clearTimeout(advertiserSearchTimer.current) }
  }, [advertiserSearch, advertiserOpen, isAdminUser, fetchAdvertisers])

  // Reset campaign search when advertiser changes
  useEffect(() => {
    setCampaignSearch('')
  }, [selectedAdvertiser])

  // Debounce campaign search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCampaignSearch(campaignSearch)
    }, 300)
    return () => clearTimeout(timer)
  }, [campaignSearch])

  // Fetch campaigns when advertiser or search changes
  useEffect(() => {
    if (!selectedAdvertiser) { setCampaigns([]); return }
    setIsLoadingCampaigns(true)
    const trimmed = debouncedCampaignSearch.trim().replace(/^#/, '')
    const isNumericId = /^\d+$/.test(trimmed)
    Promise.all([
      campaignApi.getCampaigns({
        page: 1,
        advertiser_id: selectedAdvertiser.id,
        q: trimmed || undefined,
        search_by: trimmed ? 'Campaigns' : undefined,
      }),
      isNumericId
        ? campaignApi.getCampaign(Number(trimmed))
            .then(c => c ? [c] : [])
            .catch(err => {
              console.warn('Campaign ID lookup failed', err)
              return [] as Campaign[]
            })
        : Promise.resolve([] as Campaign[]),
    ])
      .then(([nameResults, idResult]) => {
        const merged = [...idResult, ...nameResults.campaigns]
        const seen = new Set<number>()
        setCampaigns(merged.filter(c => seen.has(c.id) ? false : (seen.add(c.id), true)))
      })
      .catch(err => console.warn('Campaign search failed', err))
      .finally(() => setIsLoadingCampaigns(false))
  }, [selectedAdvertiser, debouncedCampaignSearch])

  // Fetch ad units for all selected campaigns
  useEffect(() => {
    if (!selectedCampaignIds.length) { setAdUnits([]); setAdUnitSearch(''); return }
    setIsLoadingAdUnits(true)
    Promise.all(
      selectedCampaignIds.map(id => adUnitsApi.getAdUnits(id, { per_page: 100 }))
    )
      .then(results => {
        const merged = results.flatMap(r => r.adUnits)
        const seen = new Set<number>()
        setAdUnits(merged.filter(u => seen.has(u.id) ? false : (seen.add(u.id), true)))
      })
      .catch(() => {})
      .finally(() => setIsLoadingAdUnits(false))
    setAdUnitSearch('')
  }, [selectedCampaignIds])

  function handlePreset(preset: DatePreset) {
    setDatePreset(preset)
    const dates = getPresetDates(preset)
    if (dates) {
      onDateFromChange(dates.from)
      onDateToChange(dates.to)
    }
  }

  function toggleCampaign(id: number) {
    if (selectedCampaignIds.includes(id)) {
      onCampaignIdsChange(selectedCampaignIds.filter((x) => x !== id))
    } else {
      onCampaignIdsChange([...selectedCampaignIds, id])
    }
    onAdUnitIdsChange([])
  }

  function toggleAllCampaigns() {
    if (selectedCampaignIds.length === campaigns.length) {
      onCampaignIdsChange([])
    } else {
      onCampaignIdsChange(campaigns.map((c) => c.id))
    }
    onAdUnitIdsChange([])
  }

  function toggleAdUnit(id: number) {
    if (selectedAdUnitIds.includes(id)) {
      onAdUnitIdsChange(selectedAdUnitIds.filter((x) => x !== id))
    } else {
      onAdUnitIdsChange([...selectedAdUnitIds, id])
    }
  }

  const campaignLabel =
    selectedCampaignIds.length === 0
      ? 'All campaigns'
      : selectedCampaignIds.length === campaigns.length
      ? 'All campaigns'
      : `${selectedCampaignIds.length} selected`

  const adUnitLabel =
    selectedAdUnitIds.length === 0
      ? 'All ad units'
      : `${selectedAdUnitIds.length} selected`

  const filteredAdUnits = adUnitSearch
    ? adUnits.filter(u => {
        const q = adUnitSearch.toLowerCase()
        return (u.name ?? '').toLowerCase().includes(q) || u.id.toString().includes(q)
      })
    : adUnits

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl mb-5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filters</span>
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Row 1: Dropdowns */}
          <div className="flex flex-wrap gap-3 items-end">
            {/* Advertiser */}
            <div className="flex flex-col gap-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Advertiser <span className="text-red-500">*</span>
              </label>
              {isAdminUser ? (
                <div ref={advertiserRef} className="relative">
                  <button
                    onClick={() => setAdvertiserOpen((v) => !v)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selectedAdvertiser
                        ? 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-gray-900 dark:text-white'
                        : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <span className="truncate">
                      {selectedAdvertiser ? selectedAdvertiser.name : 'Select advertiser'}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {selectedAdvertiser && (
                        <span
                          onClick={(e) => { e.stopPropagation(); onAdvertiserChange(null) }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" />
                        </span>
                      )}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </div>
                  </button>

                  {advertiserOpen && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Search by name or ID..."
                          value={advertiserSearch}
                          onChange={(e) => setAdvertiserSearch(e.target.value)}
                          className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      <div className="max-h-52 overflow-y-auto py-1">
                        {isLoadingAdvertisers ? (
                          <div className="px-3 py-2 text-sm text-gray-400">Loading...</div>
                        ) : advertisers.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-400">No advertisers found</div>
                        ) : (
                          advertisers.map((a) => (
                            <button
                              key={a.id}
                              onClick={() => { onAdvertiserChange(a); setAdvertiserOpen(false); setAdvertiserSearch('') }}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between gap-2 ${
                                selectedAdvertiser?.id === a.id ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-800 dark:text-gray-200'
                              }`}
                            >
                              <span className="truncate">{a.name}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">#{a.id}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300">
                  {selectedAdvertiser?.name ?? '—'}
                </div>
              )}
            </div>

            {/* Campaigns */}
            <div className="flex flex-col gap-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Campaigns</label>
              <div ref={campaignRef} className="relative">
                <button
                  disabled={!selectedAdvertiser}
                  onClick={() => setCampaignOpen((v) => !v)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedCampaignIds.length > 0
                      ? 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-gray-900 dark:text-white'
                      : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="truncate">{campaignLabel}</span>
                  <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                </button>

                {campaignOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search by name or ID..."
                        value={campaignSearch}
                        onChange={(e) => setCampaignSearch(e.target.value)}
                        className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      {isLoadingCampaigns ? (
                        <div className="px-3 py-2 text-sm text-gray-400">Loading...</div>
                      ) : campaigns.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No campaigns found</div>
                      ) : (
                        <>
                          <button
                            onClick={toggleAllCampaigns}
                            className="w-full text-left px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800"
                          >
                            {selectedCampaignIds.length === campaigns.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {campaigns.map((c) => (
                            <label
                              key={c.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedCampaignIds.includes(c.id)}
                                onChange={() => toggleCampaign(c.id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1">{c.name}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">#{c.id}</span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ad Units */}
            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Ad Units</label>
              <div ref={adUnitRef} className="relative">
                <button
                  disabled={!selectedCampaignIds.length}
                  onClick={() => setAdUnitOpen((v) => !v)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    selectedAdUnitIds.length > 0
                      ? 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-gray-900 dark:text-white'
                      : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="truncate">{adUnitLabel}</span>
                  <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                </button>

                {adUnitOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search by name or ID..."
                        value={adUnitSearch}
                        onChange={(e) => setAdUnitSearch(e.target.value)}
                        className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      {isLoadingAdUnits ? (
                        <div className="px-3 py-2 text-sm text-gray-400">Loading...</div>
                      ) : filteredAdUnits.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">{adUnitSearch ? 'No matches' : 'No ad units found'}</div>
                      ) : (
                        filteredAdUnits.map((u) => (
                          <label
                            key={u.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAdUnitIds.includes(u.id)}
                              onChange={() => toggleAdUnit(u.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1">
                              {u.name ?? `Ad Unit ${u.id}`}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">#{u.id}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Date range + Timezone */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Date range</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { onDateFromChange(e.target.value); setDatePreset('custom') }}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-gray-400 text-sm">→</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { onDateToChange(e.target.value); setDatePreset('custom') }}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex gap-1 pb-0.5">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => handlePreset(p.value)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    datePreset === p.value
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-600'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Timezone */}
            <div className="flex flex-col gap-1 min-w-[210px]">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Timezone</label>
              <div ref={timezoneRef} className="relative">
                <button
                  onClick={() => { setTimezoneOpen(v => !v); setTimezoneSearch('') }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    timezone
                      ? 'border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-gray-900 dark:text-white'
                      : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="truncate">{timezoneDisplayLabel}</span>
                  <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                </button>

                {timezoneOpen && (
                  <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search timezone..."
                        value={timezoneSearch}
                        onChange={(e) => setTimezoneSearch(e.target.value)}
                        className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto py-1">
                      {filteredTimezones.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No timezones found</div>
                      ) : (
                        filteredTimezones.map((tz) => (
                          <button
                            key={tz.value}
                            onClick={() => { onTimezoneChange(tz.value); setTimezoneOpen(false); setTimezoneSearch('') }}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between gap-2 ${
                              timezone === tz.value ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            <span className="truncate">{tz.label}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{tz.offset}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Breakdown + Metrics + Run */}
          <div className="space-y-3 pt-1">
            <BreakdownSelector selected={breakdowns} onChange={onBreakdownsChange} />
            <div className="flex flex-wrap items-center justify-between gap-4">
              <MetricSelector
                selectedMetrics={selectedMetrics}
                onChange={onMetricsChange}
              />
              <button
                onClick={onRunReport}
                disabled={!selectedAdvertiser || isLoading}
                className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Run Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
